/**
 * Cuánto queso van a llevar: en gramos o en dinero, sin abrir ninguna ventana.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO NO ES UNA VENTANA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * La primera versión era un cuadro que se abría encima. Funcionaba, y el dueño
 * puso el dedo en el problema con un ejemplo: una charcutería despacha «500 de
 * queso amarillo, 500 de queso para rallar, 500 de jamón, un kilo de carne» de
 * corrido. Con un cuadro, eso son cuatro ventanas que se abren, tapan la
 * pantalla y se cierran — cuatro veces la misma animación, y en tablet cuatro
 * veces el teclado subiendo y bajando.
 *
 * Esta franja aparece DEBAJO DEL BUSCADOR, dentro del flujo: no oscurece nada,
 * no tapa la cuadrícula y no bloquea. Y lo importante: **no se cierra al
 * agregar**. Se queda puesta, así que despachar cuatro cosas al peso es
 * teclear-Enter, tocar el siguiente, teclear-Enter. La franja solo cambia de
 * nombre.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  LAS DOS FORMAS DE PEDIR, Y POR QUÉ ESTÁN LAS DOS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * En el mostrador se pide de dos maneras y ninguna gana:
 *
 *   · **«Medio kilo de queso»** — la persona sabe cuánto quiere.
 *   · **«Dame dos dólares de queso»** — la persona sabe cuánto va a gastar.
 *
 * La segunda es la más común en una bodega y era la que no existía: el cajero
 * dividía de cabeza, rápido y con gente esperando. Es justo donde más se falla,
 * y el error se va con el cliente.
 *
 * Las dos casillas están a la vez y enlazadas: se escribe en una y la otra se
 * llena sola. Sin elegir un modo antes de empezar, que es un paso de más en la
 * pantalla donde menos sobran los pasos.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y HAY UNA VERSIÓN CON UNA CASILLA MENOS: EL CABLE, LA TELA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Un cable se vende por metros y también se vende partido, pero **nadie pide
 * «250 centímetros de cable»**: pide dos metros y medio. Ahí la casilla de la
 * unidad chica no significa nada, y los atajos de ¼ ½ ¾ tampoco.
 *
 * Por eso esa unidad estaba fuera de la franja entera — y con ella se iba lo
 * que más sirve, que no es la casilla de los gramos: es la del dinero.
 *
 *     «Dame tres dólares de cable»
 *
 * Eso en una ferretería se dice todos los días. Sin la franja, el cajero divide
 * de cabeza —el metro a 1,20, tres dólares son dos metros y medio— con gente
 * esperando. Es la misma cuenta que la franja le quita al de la charcutería.
 *
 * Así que para metros, gramos y mililitros se pinta la misma franja sin los
 * atajos y con la primera casilla en su propia unidad. Lo que se escribe ahí
 * son metros, no centímetros.
 */

import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

import { Boton, cx } from "@/components/ui";
import { formatMoney, type FormatoMoneda } from "@/lib/money";
import { useLimpiarMonto } from "@/lib/pasoDeMoneda";
import type { ItemListRow } from "@/lib/types";
import {
  cantidadDesdeDinero,
  cantidadDesdeMenuda,
  comoSeDice,
  etiquetaDeUnidad,
  dineroDesdeCantidad,
  menudaDesdeCantidad,
  precioFinalPorUnidad,
  unidadDePeso,
} from "./pesoYDinero";

type Props = {
  item: ItemListRow;
  formato: FormatoMoneda;
  onAgregar: (cantidad: number) => void;
  onCerrar: () => void;
};

/**
 * Lo que se pide de un toque en cualquier charcutería, en partes de la unidad
 * de venta: un cuarto, medio, tres cuartos y uno.
 *
 * ⚠️ SE ENSEÑAN COMO NÚMEROS —250, 500, 750, 1000— Y NO COMO ¼ ½ ¾ 1.
 *
 * Lo pidió el dueño y tiene razón por dos motivos. Uno: los símbolos de
 * fracción son de libro de matemáticas, y quien está detrás del mostrador con
 * cola no está para descifrarlos. Y dos, que pesa más — **«500» es la palabra
 * que dice el cliente**. Nadie pide «un cuarto de queso»: pide quinientos. El
 * botón tiene que decir lo mismo que se oye, no su equivalente en fracciones.
 */
const ATAJOS = [0.25, 0.5, 0.75, 1];

export function BarraDePeso({ item, formato, onAgregar, onCerrar }: Props) {
  /*
    `unidad` solo existe para el kilo y el litro: son las dos que tienen una
    unidad chica que la gente use. Para los metros, los gramos y los mililitros
    vale `null`, y eso es lo que decide cuál de las dos franjas se pinta.
  */
  const unidad = unidadDePeso(item.unit);
  const enMenuda = unidad !== null;

  const menuda = unidad?.menuda ?? "";
  const corta = unidad?.corta ?? etiquetaDeUnidad(item.unit);
  const porUnidad = unidad?.porUnidad ?? 1;

  const precio = precioFinalPorUnidad(
    Number.parseFloat(item.base_price) || 0,
    Number.parseFloat(item.tax_rate ?? "0") || 0,
    item.tax_inclusive ?? true,
  );

  /*
    ⚠️ SE GUARDA LA CANTIDAD, NO LO ESCRITO EN CADA CASILLA.

    Con dos estados —uno por casilla— habría que sincronizarlos a mano, y ahí
    aparece el clásico: escribes 400, la casilla de dinero se rellena, eso
    dispara el recálculo del peso, y el número te salta bajo el dedo mientras
    escribes. Con una sola verdad, cada casilla la enseña a su manera y ninguna
    pisa a la otra.
  */
  const limpiar = useLimpiarMonto();
  const [cantidad, setCantidad] = useState(0);
  const [escribiendo, setEscribiendo] = useState<"peso" | "dinero" | null>(null);
  const [textoPeso, setTextoPeso] = useState("");
  const [textoDinero, setTextoDinero] = useState("");

  const primera = useRef<HTMLInputElement>(null);

  /*
    Al cambiar de producto se limpia y se vuelve a enfocar.

    Es lo que permite despachar de corrido: se toca el jamón y la franja ya
    está lista para el número siguiente, sin arrastrar los 500 gramos del queso
    anterior — que sería el fallo silencioso de este diseño.
  */
  useEffect(() => {
    setCantidad(0);
    setEscribiendo(null);
    setTextoPeso("");
    setTextoDinero("");
    primera.current?.focus();
    primera.current?.select();
  }, [item.id]);

  function ponerPeso(texto: string) {
    setEscribiendo("peso");
    setTextoPeso(texto);
    const escrito = Number.parseFloat(texto.replace(",", "."));
    /*
      ⚠️ Con unidad chica hay que convertir; sin ella, lo escrito YA ES la
      cantidad. Escribir «2,5» en un cable son dos metros y medio, no dos mil
      quinientos de nada.
    */
    setCantidad(
      enMenuda
        ? cantidadDesdeMenuda(escrito, porUnidad)
        : Number.isFinite(escrito) && escrito > 0
          ? escrito
          : 0,
    );
  }

  function ponerDinero(texto: string) {
    setEscribiendo("dinero");
    setTextoDinero(texto);
    setCantidad(cantidadDesdeDinero(Number.parseFloat(texto.replace(",", ".")), precio));
  }

  function ponerAtajo(parte: number) {
    setEscribiendo(null);
    setCantidad(parte);
    setTextoPeso(String(menudaDesdeCantidad(parte, porUnidad)));
    setTextoDinero(String(dineroDesdeCantidad(parte, precio)));
  }

  /*
    Lo que va en la primera casilla: gramos enteros con unidad chica, y la
    cantidad tal cual sin ella.

    ⚠️ `menudaDesdeCantidad` REDONDEA a enteros, porque los gramos son enteros.
    Usándola para los metros, «2,5 m» se vería como «3 m» — y se cobrarían dos
    y medio. La pantalla diría una cosa y la caja otra.
  */
  const enLaPrimera = enMenuda
    ? menudaDesdeCantidad(cantidad, porUnidad)
    : Math.round(cantidad * 1000) / 1000;
  const cuesta = dineroDesdeCantidad(cantidad, precio);

  function confirmar(e: React.FormEvent) {
    e.preventDefault();
    if (cantidad <= 0) return;
    onAgregar(cantidad);
    // No se cierra: se limpia y se queda lista. Ver el encabezado.
    setCantidad(0);
    setEscribiendo(null);
    setTextoPeso("");
    setTextoDinero("");
    primera.current?.focus();
  }

  return (
    <form
      onSubmit={confirmar}
      /*
        `border-accent` y fondo suave: tiene que distinguirse del buscador que
        queda justo encima, o parecen dos partes de lo mismo y el cajero escribe
        el peso donde va el código de barras.
      */
      className="animar-entrada rounded-[var(--radius-card)] border border-accent bg-accent-soft p-3"
    >
      <div className="flex flex-wrap items-end gap-3">
        {/* Qué se está pesando */}
        <div className="min-w-0 flex-1 basis-full sm:basis-auto">
          <p className="truncate text-sm font-semibold text-ink">{item.name}</p>
          <p className="tabular text-xs text-muted">
            {formatMoney(precio, formato)} por {corta}
          </p>
        </div>

        {/*
          Atajos, en los mismos números que dice el cliente.

          ⚠️ Solo con unidad chica. «¼ de metro» no lo pide nadie, y unos
          botones que nadie va a tocar le roban el sitio a la casilla del
          dinero, que en metros es justo la que importa.
        */}
        {enMenuda && (
        <div className="flex shrink-0 gap-1">
          {ATAJOS.map((parte) => (
            <button
              key={parte}
              type="button"
              onClick={() => ponerAtajo(parte)}
              className={cx(
                "pulsable tabular h-10 min-w-[3.75rem] rounded-[var(--radius-control)] border px-2 text-sm font-semibold transition-colors",
                cantidad === parte
                  ? "border-accent bg-accent text-on-accent"
                  : "border-line-strong bg-surface text-ink-soft hover:border-accent",
              )}
            >
              {comoSeDice(parte, unidad ?? { corta, menuda, porUnidad })}
            </button>
          ))}
        </div>
        )}

        {/*
          Las dos casillas y su «o» van juntas en un solo bloque.

          Sueltas, al envolverse en pantalla estrecha el «o» se quedaba colgando
          al final de la primera fila y la casilla del dinero empezaba la
          siguiente — se leía «0 g o» / «$0 Agregar», que no significa nada.
          Agrupadas, o caben las dos o bajan las dos.
        */}
        <div className="flex shrink-0 items-center gap-2">
          <label className="flex h-10 items-center gap-1.5 rounded-[var(--radius-control)] border border-line-strong bg-surface px-2.5 focus-within:border-accent">
            <input
              ref={primera}
              type="text"
              inputMode="decimal"
              value={escribiendo === "dinero" ? (enLaPrimera || "") : textoPeso}
              onChange={(e) => ponerPeso(e.target.value)}
              placeholder="0"
              aria-label={`Cuánto lleva, en ${enMenuda ? menuda : corta}`}
              className="tabular w-16 bg-transparent text-base outline-none"
            />
            <span className="text-xs text-muted">{enMenuda ? menuda : corta}</span>
          </label>

          <span className="text-xs text-muted">o</span>

          <label className="flex h-10 items-center gap-1.5 rounded-[var(--radius-control)] border border-line-strong bg-surface px-2.5 focus-within:border-accent">
            <span className="text-xs text-muted">{formato.symbol}</span>
            <input
              type="text"
              inputMode="decimal"
              value={escribiendo === "peso" ? (cuesta || "") : textoDinero}
              onChange={(e) => ponerDinero(limpiar(e.target.value, formato.code))}
              placeholder="0"
              aria-label="O cuánto va a gastar"
              className="tabular w-16 bg-transparent text-base outline-none"
            />
          </label>
        </div>

        <Boton type="submit" disabled={cantidad <= 0} className="h-10 shrink-0">
          <Check size={16} />
          Agregar
        </Boton>

        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="h-10 shrink-0 rounded-[var(--radius-control)] px-2 text-muted transition-colors hover:bg-surface-hover hover:text-ink"
        >
          <X size={17} />
        </button>
      </div>
    </form>
  );
}
